/**
 * RAGGuideModal Component
 * Modal with practical guide for structuring RAG knowledge bases
 */

import { useState, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  IconBook2,
  IconChevronDown,
  IconChevronRight,
  IconBulb,
  IconAlertTriangle,
  IconCircleCheck,
  IconDownload,
  IconFileSpreadsheet,
} from '@tabler/icons-react';
import './UserGuideModal.css';

// ===============================================
// SECTION DATA
// ===============================================

const SECTIONS = [
  {
    id: 'why-errors',
    title: 'PARTE 1 — Por que o agente erra?',
    content: (
      <>
        <p>
          O agente de IA não é burro. Quando ele inventa preços, troca modelos ou responde informações erradas,
          o problema quase nunca está no agente — <strong>está na base de dados que alimenta ele</strong>.
        </p>
        <p>
          O RAG (Retrieval-Augmented Generation) é o sistema que busca informações na sua base de dados e entrega
          ao agente para que ele responda o cliente. Se a base está desorganizada, o RAG entrega dados errados,
          e o agente responde errado.
        </p>

        <h4>O que acontece por dentro</h4>
        <ol>
          <li>O cliente pergunta: "Quanto custa a bateria do iPhone 11 Pro?"</li>
          <li>O RAG busca na sua base os registros mais parecidos com essa pergunta</li>
          <li>O RAG entrega ao agente os registros que encontrou</li>
          <li>O agente monta a resposta com base nesses registros</li>
        </ol>

        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            Se no passo 2 o RAG trouxer registros do iPhone XR junto com os do iPhone 11 Pro
            (porque ambos contêm as palavras "BATERIA IPHONE"), o agente vai misturar os preços
            — e a resposta sai errada.
          </div>
        </div>

        <h4>Exemplo real de erro</h4>
        <p><strong>Base DESORGANIZADA</strong> (tudo numa coluna só):</p>
        <table className="guide-table">
          <thead>
            <tr><th>Descrição</th><th>Marca</th><th>Valor</th><th>Estoque</th><th></th></tr>
          </thead>
          <tbody>
            <tr>
              <td>BATERIA IPHONE 11 PRO</td><td>FOXCONN</td><td>88</td><td>6</td>
              <td><span className="guide-tag guide-tag--red">❌</span></td>
            </tr>
            <tr>
              <td>BATERIA IPHONE XR</td><td>FOXCONN</td><td>69</td><td>6</td>
              <td><span className="guide-tag guide-tag--slate">INVASOR</span></td>
            </tr>
          </tbody>
        </table>
        <p>Resultado: O agente responde R$ 69 para o iPhone 11 Pro (preço do XR). O cliente recebe informação errada.</p>

        <p><strong>Base ESTRUTURADA</strong> (colunas separadas):</p>
        <table className="guide-table guide-table--example">
          <thead>
            <tr><th>Descrição</th><th>Marca Ap.</th><th>Modelo</th><th>Qualidade</th><th>Valor</th><th>Est.</th><th></th></tr>
          </thead>
          <tbody>
            <tr>
              <td>BATERIA IPHONE 11 PRO</td><td>IPHONE</td><td>11 PRO</td><td>FOXCONN</td><td>88</td><td>6</td>
              <td><span className="guide-tag guide-tag--green">✅ CORRETO</span></td>
            </tr>
            <tr>
              <td>BATERIA IPHONE XR</td><td>IPHONE</td><td>XR</td><td>FOXCONN</td><td>69</td><td>6</td>
              <td><span className="guide-tag guide-tag--slate">FILTRADO</span></td>
            </tr>
          </tbody>
        </table>
        <p>Resultado: O RAG filtra por Modelo = "11 PRO" e retorna apenas o registro correto. O XR nem aparece.</p>

        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            A diferença entre um agente que funciona e um que alucina está na <strong>estrutura da base</strong>,
            não na inteligência do agente.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'golden-rules',
    title: 'PARTE 2 — As 8 Regras de Ouro',
    content: (
      <>
        <p>
          Estas regras valem para <strong>QUALQUER nicho</strong>. Se seguir todas, seu agente já vai responder
          corretamente na grande maioria dos casos.
        </p>

        <div className="guide-problem">
          <h4>REGRA 1 — Uma linha = um item consultável</h4>
          <p>
            Cada linha da planilha deve representar exatamente <strong>UM produto/serviço</strong> com <strong>UM preço</strong>.
            Se o mesmo produto tem 3 variações de qualidade, são 3 linhas.
          </p>
        </div>

        <div className="guide-problem">
          <h4>REGRA 2 — Um preço por linha</h4>
          <p>
            Nunca coloque múltiplos preços em um campo só (ex: "R$ 69 | R$ 75 | R$ 85").
            O agente não sabe qual escolher e vai chutar. Cada preço = uma linha própria.
          </p>
        </div>

        <div className="guide-problem">
          <h4>REGRA 3 — Colunas separadas por tipo de informação</h4>
          <p>
            Cada informação importante deve ter sua própria coluna: descrição, marca, modelo, qualidade,
            preço, estoque, status. Nunca misture dados diferentes no mesmo campo.
          </p>
        </div>

        <div className="guide-problem">
          <h4>REGRA 4 — Padronize o texto</h4>
          <p>
            Use TUDO EM MAIÚSCULAS ou tudo em minúsculas — nunca misture (Inox, INOX, inox).
            Inconsistência confunde a busca. Escolha um padrão e siga em toda a planilha.
          </p>
        </div>

        <div className="guide-problem">
          <h4>REGRA 5 — Zero linhas vazias ou incompletas</h4>
          <p>
            Se um produto não tem preço, não inclua na base. Linhas vazias, campos "a combinar"
            ou "consulte" são lixo para o RAG — o agente pode retornar esses registros e travar.
          </p>
        </div>

        <div className="guide-problem">
          <h4>REGRA 6 — Não abrevie</h4>
          <p>
            Escreva "QUALIDADE NACIONAL" e não "QUAL. NACIONAL". O agente busca por texto —
            abreviações diferentes geram resultados diferentes. Um padrão, um nome, sempre igual.
          </p>
        </div>

        <div className="guide-problem">
          <h4>REGRA 7 — Inclua status ou estoque</h4>
          <p>
            Sempre tenha uma coluna que indique se o item está disponível. Pode ser estoque numérico
            (0, 5, 37) ou status textual (DISPONÍVEL, INDISPONÍVEL). Sem isso, o agente apresenta
            produtos que você não tem.
          </p>
        </div>

        <div className="guide-problem">
          <h4>REGRA 8 — Links de imagens completos e seguros</h4>
          <p>
            Se a sua planilha tem uma coluna de foto ou imagem do produto, o link precisa seguir duas regras:
          </p>
          <ol>
            <li><strong>Usar HTTPS</strong> (com S) — links com HTTP sem o S podem ser bloqueados pelo WhatsApp e navegadores, gerando erro no envio da imagem.</li>
            <li><strong>Terminar com a extensão do arquivo</strong> — o link deve terminar com <code>.jpg</code>, <code>.jpeg</code>, <code>.png</code>, <code>.webp</code> ou outra extensão de imagem. Sem a extensão, o sistema não consegue identificar o tipo do arquivo e a imagem não é exibida.</li>
          </ol>
          <table className="guide-table">
            <thead><tr><th><span className="guide-tag guide-tag--red">Errado</span></th><th><span className="guide-tag guide-tag--green">Correto</span></th></tr></thead>
            <tbody>
              <tr><td>http://meusite.com/img/produto1</td><td>https://meusite.com/img/produto1.jpg</td></tr>
              <tr><td>http://drive.google.com/file/d/abc123</td><td>https://meusite.com/fotos/carro-azul.png</td></tr>
            </tbody>
          </table>
          <div className="guide-callout guide-callout--warning">
            <IconAlertTriangle size={16} />
            <div>
              Links do Google Drive, Dropbox ou similares geralmente <strong>não funcionam</strong> como imagem direta.
              Use links públicos que apontem diretamente para o arquivo da imagem (ex: hospedado no seu site ou em um serviço de imagens).
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'common-errors',
    title: 'PARTE 3 — Os 5 Erros Mais Comuns',
    content: (
      <>
        <p>
          Estes são os erros que mais causam alucinação nos agentes. Se você corrigir apenas estes 5,
          já elimina a maioria dos problemas.
        </p>

        <div className="guide-problem">
          <h4>Erro 1: Múltiplos preços empilhados em um campo</h4>
          <p><span className="guide-tag guide-tag--red">❌ Errado</span></p>
          <table className="guide-table">
            <thead><tr><th>Produto</th><th>Descrição</th><th>Valor</th></tr></thead>
            <tbody>
              <tr><td>FR A02</td><td>INOX COM ARO R$ 109 | INOX SEM ARO R$ 85 | CHINA R$ 64</td><td>109</td></tr>
            </tbody>
          </table>

          <p style={{ marginTop: 12 }}><span className="guide-tag guide-tag--green">✅ Correto</span></p>
          <table className="guide-table guide-table--example">
            <thead><tr><th>Descrição</th><th>Modelo</th><th>Qualidade</th><th>Aro</th><th>Valor</th><th>Est.</th></tr></thead>
            <tbody>
              <tr><td>FR A02</td><td>A02</td><td>INOX</td><td>COM ARO</td><td>109</td><td>15</td></tr>
              <tr><td>FR A02</td><td>A02</td><td>INOX</td><td>SEM ARO</td><td>85</td><td>10</td></tr>
              <tr><td>FR A02</td><td>A02</td><td>CHINA</td><td>SEM ARO</td><td>64</td><td>8</td></tr>
            </tbody>
          </table>
        </div>

        <div className="guide-problem">
          <h4>Erro 2: Informações diferentes na mesma coluna</h4>
          <p>
            Uma coluna chamada "Marca" que mistura a marca do aparelho (Samsung) com a marca da peça
            (Inox, Foxconn). O agente não consegue diferenciar e mistura resultados.
          </p>
          <div className="guide-callout guide-callout--success">
            <IconCircleCheck size={16} />
            <div>
              <strong>Solução:</strong> Criar colunas separadas — "Marca_Aparelho" (Samsung, iPhone, Motorola)
              e "Qualidade" (Inox, Foxconn, Gold, Original).
            </div>
          </div>
        </div>

        <div className="guide-problem">
          <h4>Erro 3: Case inconsistente</h4>
          <table className="guide-table">
            <thead><tr><th><span className="guide-tag guide-tag--red">❌ Errado (misturado)</span></th><th><span className="guide-tag guide-tag--green">✅ Correto (padronizado)</span></th></tr></thead>
            <tbody>
              <tr><td>Inox, INOX, inox, InOx</td><td>INOX (sempre igual)</td></tr>
              <tr><td>com aro, COM ARO, Com Aro</td><td>COM ARO (sempre igual)</td></tr>
              <tr><td>QUAL. NACIONAL, Qualidade Nacional</td><td>QUALIDADE NACIONAL (sem abreviar)</td></tr>
            </tbody>
          </table>
        </div>

        <div className="guide-problem">
          <h4>Erro 4: Linhas vazias ou campos incompletos</h4>
          <p>
            Linhas com produto mas sem preço, campos com "a combinar", "consultar", ou linhas
            completamente em branco. O RAG pode indexar essas linhas e retornar resultados sem dados úteis.
          </p>
          <div className="guide-callout guide-callout--success">
            <IconCircleCheck size={16} />
            <div>
              <strong>Solução:</strong> Antes de subir a base, filtrar e remover toda linha que tenha
              algum campo obrigatório vazio (especialmente preço e descrição). Se não tem preço definido,
              não inclua na base.
            </div>
          </div>
        </div>

        <div className="guide-problem">
          <h4>Erro 5: Categorias erradas ou genéricas demais</h4>
          <p>
            Classificar 300 produtos diferentes como "Acessórios" quando na verdade são telas, baterias,
            capas, cabos e placas de carga. Uma categoria genérica demais impede qualquer filtro e confunde a busca.
          </p>
          <div className="guide-callout guide-callout--success">
            <IconCircleCheck size={16} />
            <div>
              <strong>Solução:</strong> Usar categorias específicas que reflitam o tipo real do produto:
              FRONTAL, BATERIA, CABO, CAPA, PLACA DE CARGA, CARREGADOR. Quanto mais específica a categoria, melhor o filtro.
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'checklist',
    title: 'CHECKLIST FINAL',
    content: (
      <>
        <p>Antes de subir sua base para o sistema, passe por este checklist:</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '16px 0' }}>
          {[
            'Cada linha tem exatamente UM produto com UM preço?',
            'Cada informação está na sua própria coluna (descrição, marca, modelo, qualidade, preço)?',
            'O texto está padronizado (tudo maiúscula ou tudo minúscula)?',
            'Não existe nenhuma linha vazia ou campo obrigatório em branco?',
            'Não existe abreviação (QUAL. NACIONAL → QUALIDADE NACIONAL)?',
            'Existe coluna de estoque ou status de disponibilidade?',
            'As categorias são específicas (não genéricas como "Acessórios")?',
            'Nenhum campo tem múltiplos valores separados por | ou / ?',
            'Os preços são somente números (sem "R$", sem texto misturado)?',
            'Não existem erros de digitação nos nomes (FOXCONNN, NOX, etc)?',
          ].map((item, idx) => (
            <label key={idx} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '10px 14px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              fontSize: 13,
              color: '#334155',
              cursor: 'pointer',
              lineHeight: 1.5,
            }}>
              <input type="checkbox" style={{ marginTop: 3, flexShrink: 0 }} />
              <span>{item}</span>
            </label>
          ))}
        </div>

        <div className="guide-callout guide-callout--success">
          <IconCircleCheck size={16} />
          <div>
            Se todos os itens estão marcados, <strong>sua base está pronta para o RAG</strong>.
          </div>
        </div>

        <div className="guide-callout guide-callout--danger">
          <IconAlertTriangle size={16} />
          <div>
            Se algum item falhou, <strong>corrija antes de subir</strong>. Uma base mal estruturada
            gera um agente que alucina.
          </div>
        </div>
      </>
    ),
  },
];

// ===============================================
// TEMPLATE DATA (S3 direct download)
// ===============================================

const TEMPLATES = [
  { id: 1, label: "Concessionária", description: "Veículos, peças e serviços automotivos", filename: "01_concessionaria.xlsx", url: "https://mercadoagentes.s3.us-east-1.amazonaws.com/files/guides/01_concessionaria.xlsx" },
  { id: 2, label: "Celulares e Acessórios", description: "Peças, acessórios e assistência técnica", filename: "02_celulares_acessorios.xlsx", url: "https://mercadoagentes.s3.us-east-1.amazonaws.com/files/guides/02_celulares_acessorios.xlsx" },
  { id: 3, label: "Imobiliária", description: "Imóveis para venda e locação", filename: "03_imobiliaria.xlsx", url: "https://mercadoagentes.s3.us-east-1.amazonaws.com/files/guides/03_imobiliaria.xlsx" },
  { id: 4, label: "Clínica", description: "Procedimentos, consultas e serviços de saúde", filename: "04_clinica.xlsx", url: "https://mercadoagentes.s3.us-east-1.amazonaws.com/files/guides/04_clinica.xlsx" },
  { id: 5, label: "Restaurante", description: "Cardápio, pratos e delivery", filename: "05_restaurante.xlsx", url: "https://mercadoagentes.s3.us-east-1.amazonaws.com/files/guides/05_restaurante.xlsx" },
  { id: 6, label: "E-commerce", description: "Loja virtual com catálogo de produtos", filename: "06_ecommerce.xlsx", url: "https://mercadoagentes.s3.us-east-1.amazonaws.com/files/guides/06_ecommerce.xlsx" },
  { id: 7, label: "Advocacia / Contabilidade", description: "Serviços jurídicos e contábeis", filename: "07_advocacia_contabilidade.xlsx", url: "https://mercadoagentes.s3.us-east-1.amazonaws.com/files/guides/07_advocacia_contabilidade.xlsx" },
  { id: 8, label: "Escola / Curso", description: "Cursos, turmas e matrículas", filename: "08_escola_curso.xlsx", url: "https://mercadoagentes.s3.us-east-1.amazonaws.com/files/guides/08_escola_curso.xlsx" },
  { id: 9, label: "Petshop", description: "Produtos e serviços para pets", filename: "09_petshop.xlsx", url: "https://mercadoagentes.s3.us-east-1.amazonaws.com/files/guides/09_petshop.xlsx" },
  { id: 10, label: "Salão / Estética", description: "Serviços de beleza e estética", filename: "10_salao_estetica.xlsx", url: "https://mercadoagentes.s3.us-east-1.amazonaws.com/files/guides/10_salao_estetica.xlsx" },
];

// ===============================================
// ACCORDION SECTION
// ===============================================

const GuideSection = ({ section, isOpen, onToggle }) => {
  return (
    <div className={`guide-section ${isOpen ? 'guide-section--open' : ''}`}>
      <button className="guide-section__header" onClick={onToggle}>
        <span className="guide-section__title">{section.title}</span>
        {isOpen ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
      </button>
      {isOpen && (
        <div className="guide-section__content">
          {section.content}
        </div>
      )}
    </div>
  );
};

// ===============================================
// TEMPLATE CARD
// ===============================================

const TemplateCard = ({ template, onDownload }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
      onClick={() => onDownload(template)}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#f1f5f9';
        e.currentTarget.style.borderColor = '#6366f1';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#f8fafc';
        e.currentTarget.style.borderColor = '#e2e8f0';
      }}
    >
      <div style={{
        width: 38,
        height: 38,
        borderRadius: 8,
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <IconFileSpreadsheet size={20} color="#fff" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
          {template.label}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
          {template.description}
        </div>
      </div>
      <div style={{ flexShrink: 0 }}>
        <IconDownload size={18} color="#6366f1" />
      </div>
    </div>
  );
};

// ===============================================
// MAIN MODAL
// ===============================================

const RAGGuideModal = ({ show, onHide }) => {
  const [openSections, setOpenSections] = useState(['why-errors']);

  const handleDownload = useCallback((template) => {
    const link = document.createElement('a');
    link.href = template.url;
    link.download = template.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  const toggleSection = (id) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const expandAll = () => setOpenSections(SECTIONS.map((s) => s.id));
  const collapseAll = () => setOpenSections([]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="user-guide-modal"
      scrollable
    >
      <Modal.Header closeButton className="user-guide-modal__header">
        <Modal.Title className="user-guide-modal__title">
          <div className="user-guide-modal__title-icon">
            <IconBook2 size={22} />
          </div>
          Guia Prático — Estruturação de Base RAG
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="user-guide-modal__body">
        <p className="user-guide-modal__intro">
          Como organizar seus dados para que o agente de IA responda com precisão e zero alucinação.
          Clique em cada seção para expandir.
        </p>

        <div className="user-guide-modal__controls">
          <button className="user-guide-modal__control-btn" onClick={expandAll}>
            Expandir tudo
          </button>
          <button className="user-guide-modal__control-btn" onClick={collapseAll}>
            Recolher tudo
          </button>
        </div>

        <div className="guide-sections">
          {SECTIONS.map((section) => (
            <GuideSection
              key={section.id}
              section={section}
              isOpen={openSections.includes(section.id)}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>

        {/* ============ TEMPLATES SECTION ============ */}
        <div style={{
          marginTop: 24,
          padding: '20px',
          background: '#fefce8',
          border: '1px solid #fde68a',
          borderRadius: 12,
        }}>
          <h4 style={{ fontSize: 15, fontWeight: 700, color: '#92400e', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconFileSpreadsheet size={20} />
            Modelos de Planilha Prontos
          </h4>
          <p style={{ fontSize: 13, color: '#78716c', margin: '0 0 16px 0' }}>
            Baixe um modelo pronto para o seu nicho e use como base para montar sua planilha.
            Todos já seguem as 8 Regras de Ouro.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {TEMPLATES.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onDownload={handleDownload}
              />
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="user-guide-modal__footer">
        <Button variant="primary" size="sm" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RAGGuideModal;
